import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, MapPin, Stethoscope, Calendar, CreditCard, ShieldCheck, Star, ChevronRight, HeartPulse, Baby, Bone, Brain, Flower2, Ear, Eye, Smile, Sparkles } from "lucide-react";
import { api, CITIES, formatINR } from "@/lib/api";
import DoctorCard from "@/components/DoctorCard";

const ICON_MAP = {
  Cardiology: HeartPulse, Dermatology: Sparkles, Pediatrics: Baby,
  Orthopedics: Bone, Neurology: Brain, Gynecology: Flower2,
  "General Physician": Stethoscope, ENT: Ear, Ophthalmology: Eye, Dentistry: Smile,
};

export default function Home() {
  const [specialties, setSpecialties] = useState([]);
  const [topDoctors, setTopDoctors] = useState([]);
  const [q, setQ] = useState("");
  const [city, setCity] = useState("");
  const [specialty, setSpecialty] = useState("");
  const nav = useNavigate();

  useEffect(() => {
    api.get("/specialties").then((r) => setSpecialties(r.data));
    api.get("/doctors", { params: { sort: "rating" } }).then((r) => setTopDoctors(r.data.slice(0, 6)));
  }, []);

  const search = (e) => {
    e?.preventDefault();
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (city) params.set("city", city);
    if (specialty) params.set("specialty", specialty);
    nav(`/doctors?${params.toString()}`);
  };

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden hero-gradient">
        <div className="absolute inset-0 hero-grid opacity-60" />
        <div className="relative mx-auto grid max-w-7xl grid-cols-1 gap-10 px-4 py-16 sm:px-6 md:py-20 lg:grid-cols-12 lg:gap-12 lg:px-8 lg:py-24">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-4 py-1.5 text-xs font-semibold text-blue-800">
              <ShieldCheck className="h-3.5 w-3.5" />
              Trusted by 1M+ patients across India
            </div>
            <h1 className="mt-5 text-4xl font-extrabold leading-[1.08] tracking-tight text-slate-900 sm:text-5xl lg:text-6xl">
              Find the Right Doctor.
              <br />
              <span className="bg-gradient-to-r from-blue-700 to-teal-600 bg-clip-text text-transparent">
                Book Your Appointment.
              </span>
            </h1>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg">
              Discover verified doctors near you and book your consultation online in just a few clicks. Pay at clinic or online — your choice.
            </p>

            <form onSubmit={search} className="mt-8 rounded-2xl border border-slate-200 bg-white p-3 shadow-lg shadow-blue-500/5 sm:p-4">
              <div className="grid grid-cols-1 gap-2 md:grid-cols-[1.3fr_1fr_1fr_auto]">
                <div className="relative">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <input
                    data-testid="hero-search-q"
                    className="input-field pl-9"
                    placeholder="Doctor name or specialty"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                  />
                </div>
                <select
                  data-testid="hero-search-specialty"
                  className="input-field"
                  value={specialty}
                  onChange={(e) => setSpecialty(e.target.value)}
                >
                  <option value="">All Specialties</option>
                  {specialties.map((s) => <option key={s.name}>{s.name}</option>)}
                </select>
                <div className="relative">
                  <MapPin className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <select
                    data-testid="hero-search-city"
                    className="input-field pl-9"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                  >
                    <option value="">All Cities</option>
                    {CITIES.map((c) => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <button data-testid="hero-search-btn" className="btn-primary">
                  <Search className="h-4 w-4" /> Search
                </button>
              </div>
            </form>

            <div className="mt-8 grid grid-cols-3 gap-4 sm:max-w-md">
              {[
                { v: "15,000+", l: "Verified Doctors" },
                { v: "50+", l: "Cities" },
                { v: "1M+", l: "Bookings" },
              ].map((s) => (
                <div key={s.l} className="rounded-xl border border-slate-200 bg-white/70 p-3 backdrop-blur">
                  <div className="text-xl font-extrabold tracking-tight text-slate-900 sm:text-2xl">{s.v}</div>
                  <div className="text-[11px] font-medium text-slate-500 sm:text-xs">{s.l}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="relative mx-auto max-w-md lg:max-w-full">
              <div className="absolute -inset-4 rounded-[2rem] bg-gradient-to-br from-blue-200/40 to-teal-200/40 blur-2xl" />
              <img
                src="https://images.unsplash.com/photo-1758691463198-dc663b8a64e4?w=800&q=80"
                alt="Modern clinic"
                className="relative w-full rounded-3xl border border-white/50 object-cover shadow-2xl shadow-blue-500/10"
              />
              <div className="absolute -bottom-5 -left-5 flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-3 shadow-xl">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700">
                  <Calendar className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-xs font-semibold text-slate-900">Instant Booking</div>
                  <div className="text-[11px] text-slate-500">Real-time slot availability</div>
                </div>
              </div>
              <div className="absolute -right-3 top-6 flex items-center gap-2 rounded-2xl border border-slate-200 bg-white p-3 shadow-xl">
                <Star className="h-4 w-4 fill-amber-500 text-amber-500" />
                <div className="text-xs font-semibold text-slate-900">4.8/5 patient rating</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SPECIALTIES */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between">
          <div>
            <div className="text-xs font-semibold uppercase tracking-widest text-teal-700">Browse by</div>
            <h2 className="mt-1 text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Popular Medical Specialties</h2>
          </div>
          <Link to="/specialties" className="hidden text-sm font-semibold text-blue-700 hover:text-blue-800 sm:flex sm:items-center sm:gap-1">
            View all <ChevronRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
          {specialties.map((s) => {
            const Icon = ICON_MAP[s.name] || Stethoscope;
            return (
              <Link
                key={s.name}
                to={`/doctors?specialty=${encodeURIComponent(s.name)}`}
                data-testid={`specialty-${s.name}`}
                className="card group flex flex-col items-start gap-3 p-5"
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-50 text-blue-700 transition-colors group-hover:bg-blue-700 group-hover:text-white">
                  <Icon className="h-6 w-6" strokeWidth={2} />
                </div>
                <div>
                  <div className="font-semibold text-slate-900">{s.name}</div>
                  <div className="mt-0.5 text-xs text-slate-500">Book expert care</div>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* TOP DOCTORS */}
      <section className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between">
          <div>
            <div className="text-xs font-semibold uppercase tracking-widest text-teal-700">Hand-picked</div>
            <h2 className="mt-1 text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Top-Rated Doctors</h2>
          </div>
          <Link to="/doctors" className="text-sm font-semibold text-blue-700 hover:text-blue-800">See all →</Link>
        </div>
        <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {topDoctors.map((d) => <DoctorCard key={d.id} doctor={d} />)}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <h2 className="text-center text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">How Booking Works</h2>
        <div className="mx-auto mt-10 grid max-w-5xl grid-cols-1 gap-5 md:grid-cols-3">
          {[
            { icon: Search, t: "Search Doctor", d: "Filter by specialty, city, fee or experience to find your perfect match." },
            { icon: Calendar, t: "Choose Date & Slot", d: "Pick a convenient time from real-time clinic availability." },
            { icon: CreditCard, t: "Pay at Clinic or Online", d: "Flexible payment — pay instantly or at the clinic." },
          ].map((s, i) => (
            <div key={s.t} className="card relative p-6">
              <div className="absolute -top-3 left-6 flex h-7 w-7 items-center justify-center rounded-full bg-blue-700 text-xs font-bold text-white">{i + 1}</div>
              <s.icon className="h-7 w-7 text-teal-600" />
              <div className="mt-4 text-lg font-bold text-slate-900">{s.t}</div>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <h2 className="text-center text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Loved by patients across India</h2>
        <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-3">
          {[
            { name: "Rahul Sharma", city: "Mumbai", t: "Booked a cardiologist within 2 minutes. The slot was accurate and the clinic was ready for me." },
            { name: "Priya Iyer", city: "Bengaluru", t: "Love the pay-at-clinic option. Reviews and ratings helped me pick a trusted dermatologist." },
            { name: "Arjun Mehta", city: "Delhi NCR", t: "Managing appointments for my parents has become effortless with the family profiles." },
          ].map((r) => (
            <div key={r.name} className="card p-6">
              <div className="flex items-center gap-1 text-amber-500">
                {Array.from({ length: 5 }).map((_, i) => <Star key={i} className="h-4 w-4 fill-current" />)}
              </div>
              <p className="mt-4 text-sm leading-relaxed text-slate-700">"{r.t}"</p>
              <div className="mt-4 text-sm font-semibold text-slate-900">{r.name}</div>
              <div className="text-xs text-slate-500">{r.city}</div>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="mx-auto max-w-3xl px-4 py-16 sm:px-6 lg:px-8">
        <h2 className="text-center text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Frequently Asked Questions</h2>
        <div className="mt-8 space-y-3">
          {[
            { q: "Can I pay at the clinic instead of online?", a: "Yes! MediConnect supports pay-at-clinic by default for most doctors in India. You'll see a Pay at Clinic badge during booking." },
            { q: "How do I cancel or reschedule?", a: "Go to Patient Dashboard → Upcoming Appointments and tap Cancel or Reschedule. Cancellations free up the slot instantly." },
            { q: "Can I book for a family member?", a: "Absolutely. Add family members in your Patient Dashboard and select them during booking." },
            { q: "Are doctors verified?", a: "Every doctor is approved by our admin team after medical registration verification before appearing publicly." },
          ].map((f) => (
            <details key={f.q} className="group rounded-2xl border border-slate-200 bg-white p-5 transition-all open:shadow-md">
              <summary className="flex cursor-pointer items-center justify-between font-semibold text-slate-900">
                {f.q}
                <ChevronRight className="h-4 w-4 transition-transform group-open:rotate-90" />
              </summary>
              <p className="mt-3 text-sm leading-relaxed text-slate-600">{f.a}</p>
            </details>
          ))}
        </div>
      </section>
    </div>
  );
}
